/*
 * QR Code generator library (TypeScript)
 *
 * Copyright (c) Project Nayuki. (MIT License)
 * https://www.nayuki.io/page/qr-code-generator-library
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of
 * this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to
 * use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
 * the Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 * - The above copyright notice and this permission notice shall be included in
 *   all copies or substantial portions of the Software.
 * - The Software is provided "as is", without warranty of any kind, express or
 *   implied, including but not limited to the warranties of merchantability,
 *   fitness for a particular purpose and noninfringement. In no event shall the
 *   authors or copyright holders be liable for any claim, damages or other
 *   liability, whether in an action of contract, tort or otherwise, arising from,
 *   out of or in connection with the Software or the use or other dealings in the
 *   Software.
 */

/*
 * Vendored for the desk's walk-in intake code (PA-5 / EXECDEV-190), trimmed to what the desk
 * needs: QrCode.encodeText, the error correction levels and getModule. Kanji and ECI segments,
 * encodeBinary and the upstream rendering helpers are left out. The algorithm, tables and mask
 * choice are upstream's; the code is adapted to this repository's strict TypeScript (flat module
 * arrays, checked table lookups) and module syntax instead of the upstream namespace.
 */

/** The error correction level of a QR Code symbol. */
export class QrEcc {
  /** The QR Code can tolerate about 7% erroneous codewords. */
  public static readonly LOW = new QrEcc(0, 1);
  /** The QR Code can tolerate about 15% erroneous codewords. */
  public static readonly MEDIUM = new QrEcc(1, 0);
  /** The QR Code can tolerate about 25% erroneous codewords. */
  public static readonly QUARTILE = new QrEcc(2, 3);
  /** The QR Code can tolerate about 30% erroneous codewords. */
  public static readonly HIGH = new QrEcc(3, 2);

  private constructor(
    /** In the range 0 to 3 (unsigned 2-bit integer). */
    public readonly ordinal: number,
    /** In the range 0 to 3 (unsigned 2-bit integer). */
    public readonly formatBits: number,
  ) {}
}

/** Appends the given number of low-order bits of the given value to the buffer. */
function appendBits(value: number, length: number, buffer: number[]): void {
  if (length < 0 || length > 31 || value >>> length !== 0)
    throw new RangeError("Value out of range");
  for (let index = length - 1; index >= 0; index--) buffer.push((value >>> index) & 1);
}

/** True iff the i'th bit of x is set to 1. */
function getBit(x: number, index: number): boolean {
  return ((x >>> index) & 1) !== 0;
}

function assert(condition: boolean): void {
  if (!condition) throw new Error("Assertion error");
}

/** Describes how a segment's data bits are interpreted. */
class QrMode {
  public static readonly NUMERIC = new QrMode(0x1, [10, 12, 14]);
  public static readonly ALPHANUMERIC = new QrMode(0x2, [9, 11, 13]);
  public static readonly BYTE = new QrMode(0x4, [8, 16, 16]);

  private constructor(
    /** The mode indicator bits, an unsigned 4-bit value (range 0 to 15). */
    public readonly modeBits: number,
    /** Number of character count bits for three different version ranges. */
    private readonly numBitsCharCount: readonly [number, number, number],
  ) {}

  /** The bit width of the character count field for a segment in this mode at the version. */
  public numCharCountBits(version: number): number {
    const bits = this.numBitsCharCount[Math.floor((version + 7) / 17)];
    if (bits === undefined) throw new RangeError("Version number out of range");
    return bits;
  }
}

const NUMERIC_REGEX = /^[0-9]*$/;
const ALPHANUMERIC_REGEX = /^[A-Z0-9 $%*+./:-]*$/;
const ALPHANUMERIC_CHARSET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ $%*+-./:";

/** A segment of character/binary/control data in a QR Code symbol. Immutable. */
class QrSegment {
  /** A segment for the given binary data, encoded in byte mode. */
  public static makeBytes(data: Readonly<ArrayLike<number>>): QrSegment {
    const bits: number[] = [];
    for (let index = 0; index < data.length; index++) appendBits(data[index] ?? 0, 8, bits);
    return new QrSegment(QrMode.BYTE, data.length, bits);
  }

  /** A segment representing the given string of decimal digits encoded in numeric mode. */
  public static makeNumeric(digits: string): QrSegment {
    if (!NUMERIC_REGEX.test(digits)) throw new RangeError("String contains non-numeric characters");
    const bits: number[] = [];
    for (let index = 0; index < digits.length; ) {
      // Consume up to 3 digits per iteration.
      const count = Math.min(digits.length - index, 3);
      appendBits(Number.parseInt(digits.substring(index, index + count), 10), count * 3 + 1, bits);
      index += count;
    }
    return new QrSegment(QrMode.NUMERIC, digits.length, bits);
  }

  /** A segment representing the given text string encoded in alphanumeric mode. */
  public static makeAlphanumeric(text: string): QrSegment {
    if (!ALPHANUMERIC_REGEX.test(text)) {
      throw new RangeError("String contains unencodable characters in alphanumeric mode");
    }
    const bits: number[] = [];
    let index: number;
    for (index = 0; index + 2 <= text.length; index += 2) {
      // Process groups of 2.
      let temp = ALPHANUMERIC_CHARSET.indexOf(text.charAt(index)) * 45;
      temp += ALPHANUMERIC_CHARSET.indexOf(text.charAt(index + 1));
      appendBits(temp, 11, bits);
    }
    if (index < text.length) {
      // 1 character remaining.
      appendBits(ALPHANUMERIC_CHARSET.indexOf(text.charAt(index)), 6, bits);
    }
    return new QrSegment(QrMode.ALPHANUMERIC, text.length, bits);
  }

  /**
   * Zero or more segments to represent the given Unicode text string. The result may use
   * various segment modes and switch modes to optimize the length of the bit stream.
   */
  public static makeSegments(text: string): QrSegment[] {
    if (text === "") return [];
    if (NUMERIC_REGEX.test(text)) return [QrSegment.makeNumeric(text)];
    if (ALPHANUMERIC_REGEX.test(text)) return [QrSegment.makeAlphanumeric(text)];
    return [QrSegment.makeBytes(new TextEncoder().encode(text))];
  }

  private constructor(
    /** The mode indicator of this segment. */
    public readonly mode: QrMode,
    /** The length of this segment's unencoded data (characters or bytes). */
    public readonly numChars: number,
    /** The data bits of this segment. */
    private readonly bitData: readonly number[],
  ) {
    if (numChars < 0) throw new RangeError("Invalid argument");
  }

  /** A new copy of the data bits of this segment. */
  public getData(): number[] {
    return this.bitData.slice();
  }

  /**
   * The number of bits needed to encode the segments at the given version, or Infinity if a
   * segment has too many characters to fit its length field.
   */
  public static getTotalBits(segments: readonly QrSegment[], version: number): number {
    let result = 0;
    for (const segment of segments) {
      const countBits = segment.mode.numCharCountBits(version);
      if (segment.numChars >= 1 << countBits) return Number.POSITIVE_INFINITY;
      result += 4 + countBits + segment.bitData.length;
    }
    return result;
  }
}

/* Tables from the QR Code specification. Index 0 of each row pads the version number. */

// biome-ignore format: the upstream table layout, one row per error correction level
const ECC_CODEWORDS_PER_BLOCK: readonly (readonly number[])[] = [
  // Version: (note that index 0 is for padding, and is set to an illegal value)
  //0,  1,  2,  3,  4,  5,  6,  7,  8,  9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40    Error correction level
  [-1,  7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],  // Low
  [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28],  // Medium
  [-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],  // Quartile
  [-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],  // High
];

// biome-ignore format: the upstream table layout, one row per error correction level
const NUM_ERROR_CORRECTION_BLOCKS: readonly (readonly number[])[] = [
  // Version: (note that index 0 is for padding, and is set to an illegal value)
  //0, 1, 2, 3, 4, 5, 6, 7, 8, 9,10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40    Error correction level
  [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4,  4,  4,  4,  4,  6,  6,  6,  6,  7,  8,  8,  9,  9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25],  // Low
  [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5,  5,  8,  9,  9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49],  // Medium
  [-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8,  8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34, 34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68],  // Quartile
  [-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35, 37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77, 81],  // High
];

function tableValue(table: readonly (readonly number[])[], ecl: QrEcc, version: number): number {
  const value = table[ecl.ordinal]?.[version];
  if (value === undefined || value < 0) throw new RangeError("Version number out of range");
  return value;
}

// For use in getPenaltyScore(), when evaluating which mask is best.
const PENALTY_N1 = 3;
const PENALTY_N2 = 3;
const PENALTY_N3 = 40;
const PENALTY_N4 = 10;

/**
 * A QR Code symbol, which is a type of two-dimension barcode. Invented by Denso Wave and
 * described in the ISO/IEC 18004 standard. Instances of this class represent an immutable
 * square grid of dark and light cells.
 */
export class QrCode {
  /** The minimum version number supported in the QR Code Model 2 standard. */
  public static readonly MIN_VERSION = 1;
  /** The maximum version number supported in the QR Code Model 2 standard. */
  public static readonly MAX_VERSION = 40;

  /**
   * A QR Code representing the given Unicode text string at the given error correction level.
   * The smallest possible version is automatically chosen, and the error correction level may
   * be raised when that does not increase the version. Throws a RangeError when the text does
   * not fit in the largest version.
   */
  public static encodeText(text: string, ecl: QrEcc): QrCode {
    return QrCode.encodeSegments(QrSegment.makeSegments(text), ecl);
  }

  private static encodeSegments(segments: readonly QrSegment[], requestedEcl: QrEcc): QrCode {
    let ecl = requestedEcl;
    // Find the minimal version number to use.
    let version: number;
    let dataUsedBits: number;
    for (version = QrCode.MIN_VERSION; ; version++) {
      const dataCapacityBits = QrCode.getNumDataCodewords(version, ecl) * 8;
      const usedBits = QrSegment.getTotalBits(segments, version);
      if (usedBits <= dataCapacityBits) {
        dataUsedBits = usedBits;
        break; // This version number is found to be suitable.
      }
      if (version >= QrCode.MAX_VERSION) throw new RangeError("Data too long");
    }

    // Increase the error correction level while the data still fits in the current version.
    for (const newEcl of [QrEcc.MEDIUM, QrEcc.QUARTILE, QrEcc.HIGH]) {
      if (dataUsedBits <= QrCode.getNumDataCodewords(version, newEcl) * 8) ecl = newEcl;
    }

    // Concatenate all segments to create the data bit string.
    const bits: number[] = [];
    for (const segment of segments) {
      appendBits(segment.mode.modeBits, 4, bits);
      appendBits(segment.numChars, segment.mode.numCharCountBits(version), bits);
      for (const bit of segment.getData()) bits.push(bit);
    }
    assert(bits.length === dataUsedBits);

    // Add terminator and pad up to a byte if applicable.
    const dataCapacityBits = QrCode.getNumDataCodewords(version, ecl) * 8;
    assert(bits.length <= dataCapacityBits);
    appendBits(0, Math.min(4, dataCapacityBits - bits.length), bits);
    appendBits(0, (8 - (bits.length % 8)) % 8, bits);
    assert(bits.length % 8 === 0);

    // Pad with alternating bytes until data capacity is reached.
    for (let padByte = 0xec; bits.length < dataCapacityBits; padByte ^= 0xec ^ 0x11) {
      appendBits(padByte, 8, bits);
    }

    // Pack bits into bytes in big endian.
    const dataCodewords: number[] = new Array(bits.length / 8).fill(0);
    bits.forEach((bit, index) => {
      dataCodewords[index >>> 3] = (dataCodewords[index >>> 3] ?? 0) | (bit << (7 - (index & 7)));
    });

    return new QrCode(version, ecl, dataCodewords);
  }

  /** The width and height of this QR Code, measured in modules, between 21 and 177 (inclusive). */
  public readonly size: number;
  /** The index of the mask pattern used in this QR Code, which is between 0 and 7 (inclusive). */
  public readonly mask: number;

  // The modules of this QR Code (0 = light, 1 = dark), row-major.
  private readonly modules: Uint8Array;
  // Indicates function modules that are not subjected to masking. Discarded when constructor finishes.
  private readonly isFunction: Uint8Array;

  private constructor(
    /** The version number of this QR Code, which is between 1 and 40 (inclusive). */
    public readonly version: number,
    /** The error correction level used in this QR Code. */
    public readonly errorCorrectionLevel: QrEcc,
    dataCodewords: readonly number[],
  ) {
    if (version < QrCode.MIN_VERSION || version > QrCode.MAX_VERSION) {
      throw new RangeError("Version value out of range");
    }
    this.size = version * 4 + 17;
    this.modules = new Uint8Array(this.size * this.size);
    this.isFunction = new Uint8Array(this.size * this.size);

    // Compute ECC, draw modules, do masking.
    this.drawFunctionPatterns();
    const allCodewords = this.addEccAndInterleave(dataCodewords);
    this.drawCodewords(allCodewords);

    // Automatically choose the best mask.
    let mask = 0;
    let minPenalty = 1_000_000_000;
    for (let candidate = 0; candidate < 8; candidate++) {
      this.applyMask(candidate);
      this.drawFormatBits(candidate);
      const penalty = this.getPenaltyScore();
      if (penalty < minPenalty) {
        mask = candidate;
        minPenalty = penalty;
      }
      this.applyMask(candidate); // Undoes the mask due to XOR.
    }
    this.mask = mask;
    this.applyMask(mask); // Apply the final choice of mask.
    this.drawFormatBits(mask); // Overwrite old format bits.
    this.isFunction.fill(0);
  }

  /**
   * The color of the module (pixel) at the given coordinates, which is false for light or true
   * for dark. The top left corner has the coordinates (x=0, y=0). Coordinates out of bounds are
   * light.
   */
  public getModule(x: number, y: number): boolean {
    return 0 <= x && x < this.size && 0 <= y && y < this.size && this.module(x, y);
  }

  private module(x: number, y: number): boolean {
    return this.modules[y * this.size + x] === 1;
  }

  private setModule(x: number, y: number, isDark: boolean): void {
    this.modules[y * this.size + x] = isDark ? 1 : 0;
  }

  private functionModule(x: number, y: number): boolean {
    return this.isFunction[y * this.size + x] === 1;
  }

  /* ---- Private helper methods for constructor: Drawing function modules ---- */

  // Reads this object's version field, and draws and marks all function modules.
  private drawFunctionPatterns(): void {
    // Draw horizontal and vertical timing patterns.
    for (let index = 0; index < this.size; index++) {
      this.setFunctionModule(6, index, index % 2 === 0);
      this.setFunctionModule(index, 6, index % 2 === 0);
    }

    // Draw 3 finder patterns (all corners except bottom right; overwrites some timing modules).
    this.drawFinderPattern(3, 3);
    this.drawFinderPattern(this.size - 4, 3);
    this.drawFinderPattern(3, this.size - 4);

    // Draw numerous alignment patterns.
    const alignPatPos = this.getAlignmentPatternPositions();
    const numAlign = alignPatPos.length;
    for (let i = 0; i < numAlign; i++) {
      for (let j = 0; j < numAlign; j++) {
        // Don't draw on the three finder corners.
        if (
          !(
            (i === 0 && j === 0) ||
            (i === 0 && j === numAlign - 1) ||
            (i === numAlign - 1 && j === 0)
          )
        ) {
          this.drawAlignmentPattern(alignPatPos[i] ?? 0, alignPatPos[j] ?? 0);
        }
      }
    }

    // Draw configuration data.
    this.drawFormatBits(0); // Dummy mask value; overwritten later in the constructor.
    this.drawVersion();
  }

  // Draws two copies of the format bits (with its own error correction code) based on the given
  // mask and this object's error correction level field.
  private drawFormatBits(mask: number): void {
    // Calculate error correction code and pack bits.
    const data = (this.errorCorrectionLevel.formatBits << 3) | mask; // errCorrLvl is uint2, mask is uint3
    let remainder = data;
    for (let index = 0; index < 10; index++) {
      remainder = (remainder << 1) ^ ((remainder >>> 9) * 0x537);
    }
    const bits = ((data << 10) | remainder) ^ 0x5412; // uint15
    assert(bits >>> 15 === 0);

    // Draw first copy.
    for (let index = 0; index <= 5; index++) this.setFunctionModule(8, index, getBit(bits, index));
    this.setFunctionModule(8, 7, getBit(bits, 6));
    this.setFunctionModule(8, 8, getBit(bits, 7));
    this.setFunctionModule(7, 8, getBit(bits, 8));
    for (let index = 9; index < 15; index++) {
      this.setFunctionModule(14 - index, 8, getBit(bits, index));
    }

    // Draw second copy.
    for (let index = 0; index < 8; index++) {
      this.setFunctionModule(this.size - 1 - index, 8, getBit(bits, index));
    }
    for (let index = 8; index < 15; index++) {
      this.setFunctionModule(8, this.size - 15 + index, getBit(bits, index));
    }
    this.setFunctionModule(8, this.size - 8, true); // Always dark.
  }

  // Draws two copies of the version bits (with its own error correction code), based on this
  // object's version field, iff 7 <= version <= 40.
  private drawVersion(): void {
    if (this.version < 7) return;

    // Calculate error correction code and pack bits.
    let remainder = this.version; // version is uint6, in the range [7, 40]
    for (let index = 0; index < 12; index++) {
      remainder = (remainder << 1) ^ ((remainder >>> 11) * 0x1f25);
    }
    const bits = (this.version << 12) | remainder; // uint18
    assert(bits >>> 18 === 0);

    // Draw two copies.
    for (let index = 0; index < 18; index++) {
      const color = getBit(bits, index);
      const a = this.size - 11 + (index % 3);
      const b = Math.floor(index / 3);
      this.setFunctionModule(a, b, color);
      this.setFunctionModule(b, a, color);
    }
  }

  // Draws a 9*9 finder pattern including the border separator, with the center module at (x, y).
  // Modules can be out of bounds.
  private drawFinderPattern(x: number, y: number): void {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const distance = Math.max(Math.abs(dx), Math.abs(dy)); // Chebyshev/infinity norm
        const xx = x + dx;
        const yy = y + dy;
        if (0 <= xx && xx < this.size && 0 <= yy && yy < this.size) {
          this.setFunctionModule(xx, yy, distance !== 2 && distance !== 4);
        }
      }
    }
  }

  // Draws a 5*5 alignment pattern, with the center module at (x, y). All modules must be in bounds.
  private drawAlignmentPattern(x: number, y: number): void {
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) {
        this.setFunctionModule(x + dx, y + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
      }
    }
  }

  // Sets the color of a module and marks it as a function module.
  private setFunctionModule(x: number, y: number, isDark: boolean): void {
    this.setModule(x, y, isDark);
    this.isFunction[y * this.size + x] = 1;
  }

  /* ---- Private helper methods for constructor: Codewords and masking ---- */

  // Returns a new byte string representing the given data with the appropriate error correction
  // codewords appended to it, based on this object's version and error correction level.
  private addEccAndInterleave(data: readonly number[]): number[] {
    const version = this.version;
    const ecl = this.errorCorrectionLevel;
    if (data.length !== QrCode.getNumDataCodewords(version, ecl)) {
      throw new RangeError("Invalid argument");
    }

    // Calculate parameter numbers.
    const numBlocks = tableValue(NUM_ERROR_CORRECTION_BLOCKS, ecl, version);
    const blockEccLen = tableValue(ECC_CODEWORDS_PER_BLOCK, ecl, version);
    const rawCodewords = Math.floor(QrCode.getNumRawDataModules(version) / 8);
    const numShortBlocks = numBlocks - (rawCodewords % numBlocks);
    const shortBlockLen = Math.floor(rawCodewords / numBlocks);

    // Split data into blocks and append ECC to each block.
    const blocks: number[][] = [];
    const divisor = QrCode.reedSolomonComputeDivisor(blockEccLen);
    for (let index = 0, offset = 0; index < numBlocks; index++) {
      const block = data.slice(
        offset,
        offset + shortBlockLen - blockEccLen + (index < numShortBlocks ? 0 : 1),
      );
      offset += block.length;
      const ecc = QrCode.reedSolomonComputeRemainder(block, divisor);
      if (index < numShortBlocks) block.push(0);
      blocks.push(block.concat(ecc));
    }

    // Interleave (not concatenate) the bytes from every block into a single sequence.
    const result: number[] = [];
    const blockLength = blocks[0]?.length ?? 0;
    for (let index = 0; index < blockLength; index++) {
      blocks.forEach((block, blockIndex) => {
        // Skip the padding byte in short blocks.
        if (index !== shortBlockLen - blockEccLen || blockIndex >= numShortBlocks) {
          result.push(block[index] ?? 0);
        }
      });
    }
    assert(result.length === rawCodewords);
    return result;
  }

  // Draws the given sequence of 8-bit codewords (data and error correction) onto the entire data
  // area of this QR Code. Function modules need to be marked off before this is called.
  private drawCodewords(data: readonly number[]): void {
    if (data.length !== Math.floor(QrCode.getNumRawDataModules(this.version) / 8)) {
      throw new RangeError("Invalid argument");
    }
    let bitIndex = 0; // Bit index into the data.
    // Do the funny zigzag scan.
    for (let right = this.size - 1; right >= 1; right -= 2) {
      // Index of right column in each column pair.
      const column = right === 6 ? 5 : right;
      for (let vertical = 0; vertical < this.size; vertical++) {
        // Vertical counter.
        for (let offset = 0; offset < 2; offset++) {
          const x = column - offset; // Actual x coordinate.
          const upward = ((column + 1) & 2) === 0;
          const y = upward ? this.size - 1 - vertical : vertical; // Actual y coordinate.
          if (!this.functionModule(x, y) && bitIndex < data.length * 8) {
            this.setModule(x, y, getBit(data[bitIndex >>> 3] ?? 0, 7 - (bitIndex & 7)));
            bitIndex++;
          }
          // If this QR Code has any remainder bits (0 to 7), they were assigned as 0/false/light
          // by the constructor and are left unchanged by this method.
        }
      }
      right = column;
    }
    assert(bitIndex === data.length * 8);
  }

  // XORs the codeword modules in this QR Code with the given mask pattern. The function modules
  // must be marked and the codeword bits must be drawn before masking. Due to the arithmetic of
  // XOR, calling applyMask() with the same mask value a second time will undo the mask.
  private applyMask(mask: number): void {
    if (mask < 0 || mask > 7) throw new RangeError("Mask value out of range");
    for (let y = 0; y < this.size; y++) {
      for (let x = 0; x < this.size; x++) {
        let invert: boolean;
        switch (mask) {
          case 0:
            invert = (x + y) % 2 === 0;
            break;
          case 1:
            invert = y % 2 === 0;
            break;
          case 2:
            invert = x % 3 === 0;
            break;
          case 3:
            invert = (x + y) % 3 === 0;
            break;
          case 4:
            invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0;
            break;
          case 5:
            invert = ((x * y) % 2) + ((x * y) % 3) === 0;
            break;
          case 6:
            invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0;
            break;
          case 7:
            invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0;
            break;
          default:
            throw new Error("Unreachable");
        }
        if (!this.functionModule(x, y) && invert) this.setModule(x, y, !this.module(x, y));
      }
    }
  }

  // Calculates and returns the penalty score based on state of this QR Code's current modules.
  // This is used by the automatic mask choice algorithm to find the mask pattern that yields the
  // lowest score.
  private getPenaltyScore(): number {
    let result = 0;

    // Adjacent modules in row having same color, and finder-like patterns.
    for (let y = 0; y < this.size; y++) {
      let runColor = false;
      let runX = 0;
      const runHistory = [0, 0, 0, 0, 0, 0, 0];
      for (let x = 0; x < this.size; x++) {
        if (this.module(x, y) === runColor) {
          runX++;
          if (runX === 5) result += PENALTY_N1;
          else if (runX > 5) result++;
        } else {
          this.finderPenaltyAddHistory(runX, runHistory);
          if (!runColor) result += this.finderPenaltyCountPatterns(runHistory) * PENALTY_N3;
          runColor = this.module(x, y);
          runX = 1;
        }
      }
      result += this.finderPenaltyTerminateAndCount(runColor, runX, runHistory) * PENALTY_N3;
    }

    // Adjacent modules in column having same color, and finder-like patterns.
    for (let x = 0; x < this.size; x++) {
      let runColor = false;
      let runY = 0;
      const runHistory = [0, 0, 0, 0, 0, 0, 0];
      for (let y = 0; y < this.size; y++) {
        if (this.module(x, y) === runColor) {
          runY++;
          if (runY === 5) result += PENALTY_N1;
          else if (runY > 5) result++;
        } else {
          this.finderPenaltyAddHistory(runY, runHistory);
          if (!runColor) result += this.finderPenaltyCountPatterns(runHistory) * PENALTY_N3;
          runColor = this.module(x, y);
          runY = 1;
        }
      }
      result += this.finderPenaltyTerminateAndCount(runColor, runY, runHistory) * PENALTY_N3;
    }

    // 2*2 blocks of modules having same color.
    for (let y = 0; y < this.size - 1; y++) {
      for (let x = 0; x < this.size - 1; x++) {
        const color = this.module(x, y);
        if (
          color === this.module(x + 1, y) &&
          color === this.module(x, y + 1) &&
          color === this.module(x + 1, y + 1)
        ) {
          result += PENALTY_N2;
        }
      }
    }

    // Balance of dark and light modules.
    let dark = 0;
    for (const cell of this.modules) dark += cell;
    const total = this.size * this.size; // Note that size is odd, so dark/total != 1/2.
    // Compute the smallest integer k >= 0 such that (45-5k)% <= dark/total <= (55+5k)%.
    const k = Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1;
    assert(0 <= k && k <= 9);
    result += k * PENALTY_N4;
    assert(0 <= result && result <= 2568888); // Non-tight upper bound based on default values of PENALTY_N1, ..., N4.
    return result;
  }

  /* ---- Private helper functions ---- */

  // Returns an ascending list of positions of alignment patterns for this version number. Each
  // position is in the range [0,177), and are used on both the x and y axes. This could be
  // implemented as lookup table of 40 variable-length lists of integers.
  private getAlignmentPatternPositions(): number[] {
    if (this.version === 1) return [];
    const numAlign = Math.floor(this.version / 7) + 2;
    const step = Math.floor((this.version * 8 + numAlign * 3 + 5) / (numAlign * 4 - 4)) * 2;
    const result = [6];
    for (let position = this.size - 7; result.length < numAlign; position -= step) {
      result.splice(1, 0, position);
    }
    return result;
  }

  // Returns the number of data bits that can be stored in a QR Code of the given version number,
  // after all function modules are excluded. This includes remainder bits, so it might not be a
  // multiple of 8. The result is in the range [208, 29648]. This could be implemented as a
  // 40-entry lookup table.
  private static getNumRawDataModules(version: number): number {
    if (version < QrCode.MIN_VERSION || version > QrCode.MAX_VERSION) {
      throw new RangeError("Version number out of range");
    }
    let result = (16 * version + 128) * version + 64;
    if (version >= 2) {
      const numAlign = Math.floor(version / 7) + 2;
      result -= (25 * numAlign - 10) * numAlign - 55;
      if (version >= 7) result -= 36;
    }
    assert(208 <= result && result <= 29648);
    return result;
  }

  /**
   * The number of 8-bit data (not error correction) codewords contained in any QR Code of the
   * given version number and error correction level, with remainder bits discarded.
   */
  public static getNumDataCodewords(version: number, ecl: QrEcc): number {
    return (
      Math.floor(QrCode.getNumRawDataModules(version) / 8) -
      tableValue(ECC_CODEWORDS_PER_BLOCK, ecl, version) *
        tableValue(NUM_ERROR_CORRECTION_BLOCKS, ecl, version)
    );
  }

  // Returns a Reed-Solomon ECC generator polynomial for the given degree. This could be
  // implemented as a lookup table over all possible parameter values, instead of as an algorithm.
  private static reedSolomonComputeDivisor(degree: number): number[] {
    if (degree < 1 || degree > 255) throw new RangeError("Degree out of range");
    // Polynomial coefficients are stored from highest to lowest power, excluding the leading term
    // which is always 1. For example the polynomial x^3 + 255x^2 + 8x + 93 is stored as the
    // uint8 array [255, 8, 93].
    const result: number[] = [];
    for (let index = 0; index < degree - 1; index++) result.push(0);
    result.push(1); // Start off with the monomial x^0.

    // Compute the product polynomial (x - r^0) * (x - r^1) * (x - r^2) * ... * (x - r^{degree-1}),
    // and drop the highest monomial term which is always 1x^degree.
    // Note that r = 0x02, which is a generator element of this field GF(2^8/0x11D).
    let root = 1;
    for (let iteration = 0; iteration < degree; iteration++) {
      // Multiply the current product by (x - r^i).
      for (let index = 0; index < result.length; index++) {
        let term = QrCode.reedSolomonMultiply(result[index] ?? 0, root);
        if (index + 1 < result.length) term ^= result[index + 1] ?? 0;
        result[index] = term;
      }
      root = QrCode.reedSolomonMultiply(root, 0x02);
    }
    return result;
  }

  // Returns the Reed-Solomon error correction codeword for the given data and divisor polynomials.
  private static reedSolomonComputeRemainder(
    data: readonly number[],
    divisor: readonly number[],
  ): number[] {
    const result = divisor.map(() => 0);
    for (const byte of data) {
      // Polynomial division.
      const factor = byte ^ (result.shift() ?? 0);
      result.push(0);
      divisor.forEach((coefficient, index) => {
        result[index] = (result[index] ?? 0) ^ QrCode.reedSolomonMultiply(coefficient, factor);
      });
    }
    return result;
  }

  // Returns the product of the two given field elements modulo GF(2^8/0x11D). The arguments and
  // result are unsigned 8-bit integers. This could be implemented as a lookup table of 256*256
  // entries of uint8.
  private static reedSolomonMultiply(x: number, y: number): number {
    if (x >>> 8 !== 0 || y >>> 8 !== 0) throw new RangeError("Byte out of range");
    // Russian peasant multiplication.
    let z = 0;
    for (let index = 7; index >= 0; index--) {
      z = (z << 1) ^ ((z >>> 7) * 0x11d);
      z ^= ((y >>> index) & 1) * x;
    }
    assert(z >>> 8 === 0);
    return z;
  }

  // Can only be called immediately after a light run is added, and returns either 0, 1, or 2. A
  // helper function for getPenaltyScore().
  private finderPenaltyCountPatterns(runHistory: readonly number[]): number {
    const [r0 = 0, n = 0, r2 = 0, r3 = 0, r4 = 0, r5 = 0, r6 = 0] = runHistory;
    assert(n <= this.size * 3);
    const core = n > 0 && r2 === n && r3 === n * 3 && r4 === n && r5 === n;
    return (core && r0 >= n * 4 && r6 >= n ? 1 : 0) + (core && r6 >= n * 4 && r0 >= n ? 1 : 0);
  }

  // Must be called at the end of a line (row or column) of modules. A helper function for
  // getPenaltyScore().
  private finderPenaltyTerminateAndCount(
    currentRunColor: boolean,
    currentRunLength: number,
    runHistory: number[],
  ): number {
    let runLength = currentRunLength;
    if (currentRunColor) {
      // Terminate dark run.
      this.finderPenaltyAddHistory(runLength, runHistory);
      runLength = 0;
    }
    runLength += this.size; // Add light border to final run.
    this.finderPenaltyAddHistory(runLength, runHistory);
    return this.finderPenaltyCountPatterns(runHistory);
  }

  // Pushes the given value to the front and drops the last value. A helper function for
  // getPenaltyScore().
  private finderPenaltyAddHistory(currentRunLength: number, runHistory: number[]): void {
    let runLength = currentRunLength;
    if (runHistory[0] === 0) runLength += this.size; // Add light border to initial run.
    runHistory.pop();
    runHistory.unshift(runLength);
  }
}
